import { useSyncExternalStore } from 'react';

import { createExternalStore } from './createExternalStore';

const MAX_MOBILE_WIDTH = 768;
const MIN_TABLET_WIDTH = 769;
const MAX_TABLET_WIDTH = 1279;
const VIEWPORT_RESIZE_EVENT = 'resize';

const MOBILE_IDENTIFIERS = [
  'iphone',
  'ipad',
  'blackberry',
  'hammertwa',
];

type ViewportSnapshot = {
  isMobile: boolean;
  isTablet: boolean;
};

const DEFAULT_VIEWPORT_SNAPSHOT: ViewportSnapshot = {
  isMobile: false,
  isTablet: false,
};

/**
 * Detects mobile-only shells that cannot be described by viewport width alone.
 *
 * The previous hook treated specific user agents, including the embedded mobile
 * app marker, as mobile regardless of width. Keeping this as a separate helper
 * preserves that behavior while letting the viewport-width logic stay simple.
 */
const isMobileUserAgent = (): boolean => {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const userAgent = navigator.userAgent.toLowerCase();

  return MOBILE_IDENTIFIERS.some((identifier) => userAgent.includes(identifier));
};

/**
 * Reads the browser environment and builds the next breakpoint snapshot.
 *
 * This is the single source of truth for both the render-time read and the
 * resize handler, so the reported value and the update trigger both derive
 * from `window.innerWidth` and can never disagree at a breakpoint boundary.
 */
const readViewportSnapshot = (): ViewportSnapshot => {
  if (typeof window === 'undefined') {
    return DEFAULT_VIEWPORT_SNAPSHOT;
  }

  return {
    isMobile: isMobileUserAgent() || window.innerWidth <= MAX_MOBILE_WIDTH,
    isTablet: window.innerWidth >= MIN_TABLET_WIDTH && window.innerWidth <= MAX_TABLET_WIDTH,
  };
};

/**
 * Compares snapshots by value so React only hears about meaningful changes.
 *
 * `useSyncExternalStore` expects cached snapshots. Returning a new object on
 * every call would make React think the store changed continuously, so the
 * module-level snapshot is replaced only when the booleans actually differ.
 */
const areViewportSnapshotsEqual = (
  currentSnapshot: ViewportSnapshot,
  nextSnapshot: ViewportSnapshot,
): boolean => (
  currentSnapshot.isMobile === nextSnapshot.isMobile
  && currentSnapshot.isTablet === nextSnapshot.isTablet
);

/**
 * The shared viewport store backing every `useIsMobile` consumer.
 *
 * One browser-level `resize` listener serves the entire app: the first mounted
 * consumer starts it, the last unmounted consumer removes it, and the boolean
 * equality gate means consumers only re-render when a breakpoint is crossed —
 * not on every pixel of a window drag. See `createExternalStore` for the full
 * lifecycle contract.
 */
const viewportStore = createExternalStore<ViewportSnapshot>({
  eventName: VIEWPORT_RESIZE_EVENT,
  readSnapshot: readViewportSnapshot,
  defaultSnapshot: DEFAULT_VIEWPORT_SNAPSHOT,
  areSnapshotsEqual: areViewportSnapshotsEqual,
});

/**
 * Provides responsive device flags through one shared external viewport store.
 *
 * Public API is intentionally unchanged: every existing consumer can continue to
 * call `const { isMobile, isTablet } = useIsMobile()`, while React coordinates
 * updates through `useSyncExternalStore` instead of per-component resize state.
 */
const useIsMobile = (): ViewportSnapshot => {
  return useSyncExternalStore(
    viewportStore.subscribe,
    viewportStore.getSnapshot,
    viewportStore.getServerSnapshot,
  );
};

export default useIsMobile;
